import { Link } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { Empty } from 'cpool-components/state/Empty';
import { PROCESSED_NOTIFICATION_KINDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuEye, LuFileText } from 'react-icons/lu';

import { CountBadge } from '../CountBadge';
import { IconBadge } from '../IconBadge';
import Spinner from '~/components/icons/Spinner';
import { useNewDocuments } from '~/features/dashboard/hooks/useNewDocuments';
import { getNotificationLinkOptions } from '~/features/notifications/utils/getNotificationLinkOptions';
import { dateFormat, timeFormat } from '~/lib/i18n';

export const NewDocuments: React.FC = () => {
    const { t } = useTranslation();
    const { data, isError, isLoading } = useNewDocuments();

    if (!isError && (isLoading || !data)) {
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">
                <Spinner />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="py-2.5 px-5">
                <span>{t('DASHBOARD.ERROR')}</span> <br />
                <span>{t('DASHBOARD.RELOAD')}</span>
            </div>
        );
    }

    const sumOfDocuments = data.reduce((prev, currentItem) => prev + currentItem.unverifiedDocuments, 0);

    if (sumOfDocuments === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <Empty
                    noBorder
                    icon={<LuFileText />}
                    iconClassName="text-orange-500"
                    iconContainerClassName="bg-orange-500/10 rounded-lg"
                    title={t('DASHBOARD.NEWDOCUMENTS.TITLE', { count: sumOfDocuments })}
                    description={t('DASHBOARD.NEWDOCUMENTS.NO_NEW_DOCUMENTS')}
                />
            </div>
        );
    }

    return (
        <section className="py-1">
            <div className="px-5 pb-3">
                <div className="flex items-center justify-between min-h-[28px]">
                    <h6 className="text-midnight flex items-center gap-4">
                        <IconBadge icon={<LuFileText />} className="text-orange-500 bg-orange-500/10" />
                        <div className="flex flex-col">
                            <span className="text-lg font-medium leading-none mb-0.5">
                                {t('DASHBOARD.NEWDOCUMENTS.TITLE', { count: sumOfDocuments })}
                            </span>
                            <span className="text-sm">
                                {t('DASHBOARD.NEWDOCUMENTS.SUBTITLE', { count: sumOfDocuments })}
                            </span>
                        </div>
                    </h6>
                    <CountBadge count={sumOfDocuments} className="text-orange-500 bg-orange-500/10" />
                </div>
            </div>
            <div className="overflow-y-auto max-h-[180px] scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark scrollbar-track-grey-stroke scrollbar-track-rounded-md">
                <div className="px-1 flex flex-col">
                    {data?.map((item) => {
                        const createdAt = `${dateFormat(new Date(item.createdAt))} - ${timeFormat(
                            new Date(item.createdAt),
                        )}`;
                        return (
                            <Link
                                key={item.id}
                                {...getNotificationLinkOptions(PROCESSED_NOTIFICATION_KINDS.DOCUMENT, item.id)}
                                preload="intent"
                                target="_blank"
                            >
                                <div className="hover:bg-surface-light py-2 px-4 flex items-center justify-between gap-2 rounded-md">
                                    <div className="flex items-center gap-2 min-w-[200px] max-w-[370px]">
                                        <div>
                                            <h6 className="mb-1 text-sm text-midnight min-w-[200px] max-w-[200px] truncate">
                                                {item.antragsteller1.personendaten?.vorname}{' '}
                                                {item.antragsteller1.personendaten?.nachname}
                                            </h6>
                                            <p className="text-xs text-midnight-placeholder">
                                                <span>{createdAt}</span> {t('DASHBOARD.RESUBMISSIONS.DATE')}
                                            </p>
                                        </div>
                                        <span className="text-sm text-midnight">
                                            <span className="font-semibold">{item.unverifiedDocuments} </span>
                                            {t('DASHBOARD.NEWDOCUMENTS.UNVERIFIEDDOCUMENTS', {
                                                count: item.unverifiedDocuments,
                                            })}
                                        </span>
                                    </div>
                                    <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuEye />}>
                                        {t('DASHBOARD.RESUBMISSIONS.CHECKOUT')}
                                    </Button>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
